import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';

import themeable from 'zignal-landscape/lib/themeable';
import Col from 'zignal-landscape/lib/Col';
import Grid from 'zignal-landscape/lib/Grid';
import Row from 'zignal-landscape/lib/Row';
import MfeFiltersIcon from 'zignal-landscape/lib/Icon/MfeFiltersIcon';
import MfeAddProfileIcon from 'zignal-landscape/lib/Icon/MfeAddProfileIcon';
import TrashIcon from 'zignal-landscape/lib/Icon/TrashIcon';

import MfeIconWrapper from '../../components/MfeIconWrapper';
import FFBEditor from '../../components/FFBEditor';
import LanguageSelector from 'components/LanguageSelector';

import * as actions from './HistoricsSearchForm.ducks';

import { checkAdditionalFiltersForChanges } from '../../helpers';

export const mapStateToProps = (
  (state) => ({
    searchTerms: state.DiscoverHistorics.HistoricsSearchForm.searchTerms,
    translations: state.DiscoverHistorics.HistoricsSearchForm.translations,
    selectedLanguages: state.DiscoverHistorics.HistoricsSearchForm.selectedLanguages,
    areTranslationsObsolete: state.DiscoverHistorics.HistoricsSearchForm.areTranslationsObsolete,
    enabledKeywordsCount: state.DiscoverHistorics.HistoricsSearchForm.enabledKeywordsCount,
    parsingErrors: state.DiscoverHistorics.HistoricsSearchForm.parsingErrors,
    additionalFilters: state.DiscoverHistorics.HistoricsSearchForm.additionalFilters,
  })
);

export const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return Object.assign({}, stateProps, dispatchProps, ownProps);
};

const LIMIT_OF_SEARCH_TERMS = 5;
const DATEPICKER_FORMAT = 'YYYY-MM-DD+HH:mm:ss';

class HistoricsSearchForm extends React.Component {
  constructor(props) {
    super(props);
    this.state={
      options: [],
      deleteSearchIndex: -1,
      selectedIndex: -1,
      showLanguageSelector: false
    };
    this.saveAsProfile = (index, searchTerm) => {
      if (searchTerm) {
        const oneMonthAgoMoment = moment().subtract(1, 'months').startOf('day');
        const twoYearsAgoMoment = moment().subtract(2, 'years').startOf('day');
        const formattedStartDate = moment(this.props.startDate, DATEPICKER_FORMAT).format();
        // whichever is before, startDate or two months ago
        let discoverStartDate = oneMonthAgoMoment.isBefore(formattedStartDate)
          ? oneMonthAgoMoment.format(DATEPICKER_FORMAT)
          : this.props.startDate;
        // historics can't go past two years
        discoverStartDate = twoYearsAgoMoment.isAfter(formattedStartDate)
          ? twoYearsAgoMoment.format(DATEPICKER_FORMAT)
          : discoverStartDate;
        const activeCampaignId = window.Account.getActiveCampaign().get('id');
        this.props.saveAsProfile({ searchTerm, metadata: { index, discoverStartDate, activeCampaignId } });
      }
    };
  }

  componentWillUnmount() {
    this.props.clearSearchTerms();
  }

  render() {
    const {
      styles,
      addSearchTerm,
      editSearchTerm,
      onSearch,
      onTranslateKeywords,
      onToggleKeyword,
      searchTerms,
      translations,
      isFetchingTranslations,
      enabledKeywordsCount,
      colors,
      parsingErrors,
      additionalFilters,
      setAdditionalFilters,
      setTranslations,
      clearTranslations,
      setEnabledKeywordsCount,
      selectLanguages,
      removeLanguage,
      closeAdditonalFilters,
      removeSearchTerm,
      areTranslationsObsolete,
      makeTranslationsObsolete,
      availableLanguages,
      selectedLanguages,
      isRestrictedUser
    } = this.props;

    const setLanguageSelectorIndex = (index, visibleState) => {
      this.setState({
        showLanguageSelector: visibleState,
        selectedIndex: index
      });
    };

    const updateAdditionalFilters = (additionalFiltersSettings, index) => {
      const newAdditionalFilters = additionalFilters.slice();
      newAdditionalFilters[index] = additionalFiltersSettings;
      setAdditionalFilters(newAdditionalFilters);
    };
    const deleteSearchBar = (index) => {
      const newAdditionalFilters = additionalFilters.slice();
      const newTranslations = translations.slice();
      const newEnabledKeywordsCount = enabledKeywordsCount.slice();
      newAdditionalFilters.splice(index, 1);
      newAdditionalFilters.push({});
      newTranslations.splice(index, 1);
      newTranslations.push([]);
      newEnabledKeywordsCount.splice(index, 1);
      newEnabledKeywordsCount.push(0);

      setAdditionalFilters(newAdditionalFilters);
      setTranslations({ index, translations: newTranslations });
      setEnabledKeywordsCount(newEnabledKeywordsCount);
      removeSearchTerm(index);
    };

    const deleteTranslationLanguage = ({index, code}) => {
      if (translations[index].length === 2) { // only language left apart from ORIGINAL
        clearTranslations({index});
      } else {
        removeLanguage({index, code});
      }
    };

    const onSelectNewLanguages = (languages) => {
      const index = this.state.selectedIndex;
      selectLanguages({ index, languages });
      setLanguageSelectorIndex(-1, false);
      onTranslateKeywords({index, languages, searchTerm: searchTerms[index]});
    };

    const languageSelectorContainer = () => {
      const index = this.state.selectedIndex;
      const selectedLanguagesForIndex = selectedLanguages[index];
      const languageList = cloneDeep(availableLanguages);
      if (!isEmpty(selectedLanguagesForIndex)) {
        languageList.forEach((lang) => {
          lang.isSelected = selectedLanguagesForIndex.find(l => l.code === lang.code) != undefined;
        });
      }
      return (
        <LanguageSelector
          isOpen={this.state.showLanguageSelector}
          onClose={() => setLanguageSelectorIndex(-1, false)}
          languages={languageList}
          onCancelHandler={() => setLanguageSelectorIndex(-1, false)}
          onSubmitHandler={(newLanguages) => onSelectNewLanguages(newLanguages)}
        />
      );
    };

    const searchTermRows = searchTerms.map((searchTerm, index) => {
      const additionalFiltersSettings = {...additionalFilters[index]};
      const isLastRow = index === searchTerms.length - 1;
      const isRowLimitReached = searchTerms.length === LIMIT_OF_SEARCH_TERMS;

      return (
        <section key={index} className={styles.inputRow}>
          <Row
            onMouseEnter={() => this.setState({deleteSearchIndex: index})}
            onMouseLeave={() => this.setState({deleteSearchIndex: -1})}
          >
            <FFBEditor
              onModelChanged={(query) => editSearchTerm(query, index)}
              fireSearch={onSearch}
              isFetching={isFetchingTranslations}
              openLanguageSelector={() => setLanguageSelectorIndex(index, true)}
              removeLanguage={(code) => deleteTranslationLanguage({index, code})}
              translateKeywords={(languages) => onTranslateKeywords({index, languages, searchTerm})}
              toggleKeyword={(code, keywordId) => onToggleKeyword({index, code, keywordId})}
              areTranslationsObsolete={areTranslationsObsolete[index]}
              makeTranslationsObsolete={() => makeTranslationsObsolete({index})}
              index={index}
              value={searchTerm}
              error={parsingErrors[index]}
              colors={colors}
              addSearchTerm={addSearchTerm}
              placeholder={index === 0 ? 'To search across all sources, please type any keywords...' : undefined}
              additionalFiltersSettings={additionalFiltersSettings}
              updateAdditionalFilters={updateAdditionalFilters}
              closeAdditonalFilters={closeAdditonalFilters}
              translations={translations[index]}
              enabledKeywordsCount={enabledKeywordsCount[index]}
            />
            <Col className={styles.rightSection}>
              <div className={styles.iconsContainer}>
                <MfeIconWrapper
                  icon={MfeFiltersIcon}
                  open={!!additionalFiltersSettings.open}
                  showDot={checkAdditionalFiltersForChanges(additionalFiltersSettings)}
                  tooltipProps={{ tooltipText: 'Additional Filters' }}
                  onMouseDown={() => {
                    !additionalFiltersSettings.open && updateAdditionalFilters({...additionalFiltersSettings, open: true}, index);
                  }}
                />
                <div className={styles.iconSeparator} />
                <MfeIconWrapper
                  disabled={isRestrictedUser}
                  icon={MfeAddProfileIcon}
                  onClick={() => this.saveAsProfile(index, searchTerm)}
                  tooltipProps={{ tooltipText: 'Save as a Dataset' }}
                />
                <MfeIconWrapper
                  icon={TrashIcon}
                  disabled={searchTerms.length === 1}
                  onClick={() => deleteSearchBar(index)}
                />
              </div>
              {isLastRow && !isRowLimitReached &&
                <div>
                  <button className={styles.compareButton} onClick={addSearchTerm}>+ &nbsp;Compare</button>
                </div>
              }
            </Col>
          </Row>
        </section>
      );
    });

    return (
      <Grid fluid className={styles.HistoricsSearchForm}>
        {this.state.showLanguageSelector && languageSelectorContainer()}
        {searchTermRows}
      </Grid>
    );
  }
}


HistoricsSearchForm.propTypes = {
  styles: PropTypes.object,
  queryParams: PropTypes.object,
  setQueryParams: PropTypes.func.isRequired,
  addSearchTerm: PropTypes.func.isRequired,
  editSearchTerm: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onTranslateKeywords: PropTypes.func.isRequired,
  onToggleKeyword: PropTypes.func.isRequired,
  areTranslationsObsolete: PropTypes.array.isRequired,
  makeTranslationsObsolete: PropTypes.func.isRequired,
  saveAsProfile: PropTypes.func.isRequired,
  selectLanguages: PropTypes.func.isRequired,
  removeLanguage: PropTypes.func.isRequired,
  removeSearchTerm: PropTypes.func.isRequired,
  searchTerms: PropTypes.array.isRequired,
  isFetchingTranslations: PropTypes.bool.isRequired,
  translations: PropTypes.array.isRequired,
  enabledKeywordsCount: PropTypes.array.isRequired,
  colors: PropTypes.array.isRequired,
  clearSearchTerms: PropTypes.func.isRequired,
  setAdditionalFilters: PropTypes.func,
  setTranslations: PropTypes.func,
  clearTranslations: PropTypes.func,
  setEnabledKeywordsCount: PropTypes.func,
  closeAdditonalFilters: PropTypes.func,
  additionalFilters: PropTypes.array,
  parsingErrors: PropTypes.array,
  startDate: PropTypes.string,
  isRestrictedUser: PropTypes.bool
};

HistoricsSearchForm.defaultProps = {
  fetchErrorMessage: null,
  colors: [],
  translations: [],
  isRestrictedUser: false
};

export default connect(mapStateToProps, actions, mergeProps)(themeable(HistoricsSearchForm));
